"use client";

import { useState } from "react";
import { ImageLightbox, type LightboxLabels } from "@/components/ImageLightbox";
import { RetryImage } from "@/components/RetryImage";

// Review media keys can hold videos as well as photos (see ReviewController
// AllowedVideoTypes), so pick the element by extension.
const VIDEO_EXTENSIONS = /\.(mp4|mov|webm)$/i;

export function ReviewMedia({
  alt,
  enlargeLabel,
  lightboxLabels,
  sizeClassName = "h-24 w-24 md:h-28 md:w-28",
  urls,
}: {
  alt: string;
  /** Localized aria-label for the enlarge buttons; the English fallback keeps the admin usage working. */
  enlargeLabel?: string;
  lightboxLabels?: LightboxLabels;
  sizeClassName?: string;
  urls: string[];
}) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  if (!urls.length) return null;

  const images = urls.filter((url) => !VIDEO_EXTENSIONS.test(url));

  return (
    <>
      <ul className="flex flex-wrap gap-2">
        {urls.map((url) => (
          <li className={`relative overflow-hidden border border-rule bg-paper ${sizeClassName}`} key={url}>
            {VIDEO_EXTENSIONS.test(url) ? (
              <video className="h-full w-full object-cover" controls muted playsInline preload="metadata" src={url} />
            ) : (
              <button
                aria-label={enlargeLabel ?? "Enlarge photo"}
                className="relative block h-full w-full cursor-zoom-in"
                onClick={() => setLightbox(images.indexOf(url))}
                type="button"
              >
                <RetryImage alt={alt} className="object-cover" fill sizes="112px" src={url} />
              </button>
            )}
          </li>
        ))}
      </ul>

      {lightbox !== null ? (
        <ImageLightbox
          index={lightbox}
          labels={lightboxLabels}
          onClose={() => setLightbox(null)}
          onIndexChange={setLightbox}
          slides={images.map((src) => ({ src, alt }))}
        />
      ) : null}
    </>
  );
}
